// apps/client_diy_webapp/src/pages/qualitative-surveys/components/ParticipantCommunication.js
import React, { useEffect, useState } from "react";
import { RiPencilLine } from "react-icons/ri";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import SaveBar from "./Savebar";
import { useProjectUpdate } from "../useProjectUpdate";
import { EDITABLE_STATUSES } from "../qualitativeUtils";



export default function ParticipantCommunication({ projectDetails }) {
    const { user } = useGlobalContext();
    const organizationID = user?.organization?._id;
    const projectID = projectDetails?._id;
    const isEditable = EDITABLE_STATUSES?.includes(projectDetails?.status);

    const [listingName, setListingName] = useState(
        projectDetails?.participantListing?.projectListingName ?? ""
    );
    const [listingDescription, setListingDescription] = useState(
        projectDetails?.participantListing?.projectListingDescription ?? ""
    );

    useEffect(() => {
        setListingName(projectDetails?.participantListing?.projectListingName ?? "");
        setListingDescription(projectDetails?.participantListing?.projectListingDescription ?? "");
    }, [projectDetails]);

    const { save, isSaving, saveError, saveSuccess } = useProjectUpdate(organizationID, projectID);

    const handleSave = () =>
        save({
            participantListing: {
                projectListingName: listingName,
                projectListingDescription: listingDescription,
            },
        });

    return (
        <div className="space-y-6">
            {/* ── Listing Name ──────────────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-xl">
                <div className="space-y-1">
                    <h1 className="font-bold text-md">Project Listing Name</h1>
                    <p className="text-gray-500 text-xs">
                        Unique titles attract more participants. Make sure to differentiate titles from past studies.
                    </p>
                </div>
                <div className="border rounded-lg px-[14px] py-2.5 flex items-center gap-2 bg-white">
                    <input
                        type="text"
                        className="w-full text-sm placeholder:text-sm focus:outline-none bg-transparent
                            disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder="e.g. Share your experience using food delivery apps"
                        maxLength={120}
                        minLength={1}
                        name="projectListingName"
                        value={listingName}
                        onChange={(e) => isEditable && setListingName(e.target.value)}
                        disabled={!isEditable}
                    />
                    <RiPencilLine size={16} className="text-gray-300 flex-shrink-0" />
                </div>
                <div className="flex justify-end">
                    <span className="text-xs text-gray-300">{listingName.length}/120</span>
                </div>
            </div>

            {/* ── Listing Description ───────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-xl">
                <div className="space-y-1">
                    <h1 className="font-bold text-md">Project Listing Description</h1>
                    <p className="text-gray-500 text-xs">
                        Give an overview of your study and how it will be conducted. Participants appreciate
                        details, but do not include specific recruitment criteria.
                    </p>
                </div>
                <div className="border rounded-lg px-[14px] py-2.5 flex items-start gap-2 bg-white">
                    <textarea
                        className="w-full text-sm placeholder:text-sm resize-none focus:outline-none bg-transparent
                            disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder="We're looking for feedback on a product we are building."
                        rows={4}
                        maxLength={1500}
                        name="projectListingDescription"
                        value={listingDescription}
                        onChange={(e) => isEditable && setListingDescription(e.target.value)}
                        disabled={!isEditable}
                    />
                    <RiPencilLine size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                </div>
                <div className="flex justify-end">
                    <span className="text-xs text-gray-300">{listingDescription.length}/1500</span>
                </div>

                {/* Good / bad examples */}
                <div className="grid grid-cols-2 items-start gap-3">
                    <div className="border border-primary p-3 rounded-xl bg-primary/5 space-y-1">
                        <h3 className="font-semibold text-xs text-[#0226BE]">✓ Good Example</h3>
                        <p className="text-gray-700 text-xs leading-relaxed">
                            We are conducting a research session to understand how people use digital
                            products in their daily life. Selected participants will join a moderated
                            discussion and share their experiences, preferences, and feedback.
                        </p>
                    </div>
                    <div className="border border-red-300 p-3 rounded-xl bg-red-50 space-y-1">
                        <h3 className="font-semibold text-xs text-red-500">✕ Bad Example</h3>
                        <p className="text-gray-700 text-xs leading-relaxed">
                            We only need females aged 25–35 from Delhi who use Swiggy three times a week.
                        </p>
                        <p className="text-red-400 text-[10px] italic">
                            This reveals specific recruitment criteria and may bias applications.
                        </p>
                    </div>
                </div>

                {isEditable && (
                    <SaveBar
                        onSave={handleSave}
                        isSaving={isSaving}
                        saveSuccess={saveSuccess}
                        saveError={saveError}
                    />
                )}
            </div>
        </div>
    );
}